"use client"

import { useEffect } from "react"
import { SessionProvider, useSession } from "next-auth/react"

import type { ReactNode } from "react"

import { clearCachedApiToken, setActiveAuthUser } from "@/lib/auth-client"
import { AUTH_BASE_PATH } from "@/lib/auth/constants"

/**
 * BFF auth context (replaces the old MsalProvider). Mounts the Auth.js
 * SessionProvider — session state comes from the encrypted httpOnly cookie via
 * GET /auth/session — and bridges it into the non-React auth singleton that
 * api.ts/billing.ts read.
 *
 * Unlike the MSAL provider this does NOT gate children on any fetch: the
 * marketing landing at `/` paints immediately (the old landing-paints-after-
 * MSAL-init coupling is gone — the OAuth redirect now lands on
 * /auth/callback/entra, not `/`).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <SessionProvider basePath={AUTH_BASE_PATH}>
      <SessionBridge />
      {children}
    </SessionProvider>
  )
}

/** Mirrors the session into the module-level auth snapshot (auth-client.ts). */
function SessionBridge() {
  const { data: session, status } = useSession()

  useEffect(() => {
    if (status === "authenticated" && session) {
      setActiveAuthUser({
        oid: session.userId ?? null,
        email: session.user?.email ?? null,
        name: session.user?.name ?? null,
      })
    } else if (status === "unauthenticated") {
      setActiveAuthUser({ oid: null, email: null, name: null })
      clearCachedApiToken()
    }
  }, [session, status])

  return null
}
